import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Alert } from 'reactstrap';
import styled, { StyledComponentPropsWithRef } from 'styled-components';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import {
  hideLoadingSpinner,
  showLoadingSpinner,
} from '~/javascripts/admin/actions/loading-spinner';
import { useAppDispatch } from '~/javascripts/admin/hooks/useApp';
import { buildApiFormErrors } from '~/javascripts/admin/utils/apiErrors';
import { Button } from '../../atoms/Buttons';
import ButtonGroup from '../../molecules/ButtonGroup';
import AdditionalSettingsForm from './AdditionalSettingsForm';
import BasicInfoForm from './BasicInfoForm';
import ChargingMethodForm from './ChargingMethodForm';
import DeviceInfoForm from './DeviceInfoForm';

type Props = (
  | {
      mode: 'create';
      moneyFormValues?: undefined;
    }
  | {
      mode: 'edit';
      moneyFormValues: PrivateMoneyFormValues;
    }
) & {
  onConfirm: (formValues: PrivateMoneyFormValues) => Promise<void>;
  allowedInputs?: 'all' | (keyof PrivateMoneyFormValues)[];
  disabled?: boolean;
};

export type PrivateMoneyFormValues = {
  // Basic Info
  organization_code?: string;
  name: string;
  furigana: string;
  type: string;
  can_use_c2c_transfer: boolean;

  use_fallback_payment: boolean;
  fallback_private_money_uuid: string | null;

  expiration_type: string;
  expires_in: {
    amount: number;
    unit: 'months' | 'days';
  };
  expires_at: string | null;

  point_expiration_type: string;
  point_expires_in: {
    amount: number;
    unit: 'months' | 'days';
  };
  point_expires_at: string | null;

  max_balance: number;
  transfer_limit: number;
  money_topup_transfer_limit: number;
  total_topup_limit: {
    type: 'entire-period' | 'range' | null;
    amount: number | null;
    started_at: string | null;
  };

  terms: string;
  privacy_policy: string;
  payment_act: string;
  commercial_act: string;
  access_limitation: string;
  display_money_and_point: string;
  unit: string;
  description: string;
  oneline_message: string;
  square_small_image: {
    file: File | null;
    src: string | null;
  };
  payment_sound: {
    file: File | null;
    src: string | null;
  };

  // Charging Method
  can_use_credit_card: boolean;
  credit_card_daily_cap: number | null;
  credit_card_monthly_cap: number | null;
  topup_method_amounts_for_credit_card: number[];
  topup_method_amount_range_for_credit_card: [number | '', number | ''];
  require_3ds_challenge: boolean;
  can_use_bank_pay: boolean;
  bank_pay_p_id: string;
  bank_pay_iss_type: string;
  bank_pay_store_code: string;
  bank_pay_bankbook_merchant_name: string;
  can_use_seven_bank_atm: boolean;
  seven_bank_issuer_code: string;
  seven_bank_issuer_name: string;
  seven_bank_money_name: string;
  topup_method_amounts_for_seven_bank_atm: number[];

  // Additional Settings
  is_topup_quota_available: boolean;
  use_webhook_callback_url: boolean;
  transaction_webhook_callback_url: string | null;
  use_itrust_tenent: boolean;
  itrust_tenant_code: string | null;
  resident_address_pattern: string | null;
  identification_validity_days: number | null;

  // Device Info
  base_color: string;
  button_foreground_color: string;
  button_background_color: string;

  // App Info
  custom_domain_name: string;
  card_image: {
    file: File | null;
    src: string | null;
  };
};

const PRIVATE_MONEY_FORM_DEFAULT_VALUES: PrivateMoneyFormValues = {
  organization_code: '',
  id: '',
  name: '',
  furigana: '',
  type: 'own',
  can_use_c2c_transfer: false,
  use_fallback_payment: false,
  fallback_private_money_uuid: null,
  expiration_type: 'static',
  expires_in: {
    amount: 6,
    unit: 'months',
  },
  expires_at: null,
  point_expiration_type: 'static',
  point_expires_in: {
    amount: 6,
    unit: 'months',
  },
  point_expires_at: null,
  max_balance: 1000000,
  transfer_limit: 500000,
  money_topup_transfer_limit: 500000,
  total_topup_limit: {
    type: null,
    amount: null,
    started_at: null,
  },
  terms: '',
  privacy_policy: '',
  payment_act: '',
  commercial_act: '',
  access_limitation: 'unlimited',
  display_money_and_point: 'both',
  unit: '',
  description: '',
  oneline_message: '',
  square_small_image: {
    file: null,
    src: null,
  },

  payment_sound: {
    file: null,
    src: null,
  },

  can_use_credit_card: false,
  credit_card_daily_cap: null,
  credit_card_monthly_cap: null,
  topup_method_amounts_for_credit_card: [],
  topup_method_amount_range_for_credit_card: ['', ''],
  require_3ds_challenge: false,

  can_use_bank_pay: false,
  bank_pay_p_id: '',
  bank_pay_iss_type: '',
  bank_pay_store_code: '',
  bank_pay_bankbook_merchant_name: '',

  can_use_seven_bank_atm: false,
  seven_bank_issuer_code: '',
  seven_bank_issuer_name: '',
  seven_bank_money_name: '',
  topup_method_amounts_for_seven_bank_atm: [],

  is_topup_quota_available: false,
  use_webhook_callback_url: false,
  transaction_webhook_callback_url: null,
  use_itrust_tenent: false,
  itrust_tenant_code: null,
  resident_address_pattern: null,
  identification_validity_days: null,

  base_color: '#000000',
  button_foreground_color: '#000000',
  button_background_color: '#000000',

  custom_domain_name: '',
  card_image: {
    file: null,
    src: null,
  },
};

const TABS: StyledComponentPropsWithRef<typeof ButtonGroup>['buttons'] = [
  { text: '基本情報', value: 'basic-info' },
  { text: 'チャージ方法', value: 'charging-method' },
  { text: '追加機能', value: 'additional-settings' },
  { text: 'デバイス', value: 'devices' },
];

const Form = styled.form`
  margin-top: 24px;
`;

const Hidable = styled.div<{ hidden: boolean }>`
  display: ${(props) => (props.hidden ? 'none' : 'block')};
`;

const AlertMessage = styled.label`
  color: #676767;
  font-weight: bold;
`;

type ApiFields = Omit<keyof PrivateMoneyFormValues, 'total_topup_limit'> &
  'total_topup_limit_started_at';

const getApiError = buildApiFormErrors<keyof PrivateMoneyFormValues, ApiFields>({
  private_money_conflict: () => ({
    key: 'root.serverError',
    error: {
      message: 'このマネー名は既に登録されています',
    },
  }),
  invalid_parameters: (error) => {
    const key = error.errors?.invalid?.[0] ?? 'root.unknown';

    // mimic the style of error message from the previous style
    if (key === 'total_topup_limit_started_at') {
      return {
        key: 'root.serverError',
        error: {
          message: '合計チャージ上限額起算日時の入力内容に誤りがあります',
        },
      };
    }

    return {
      key,
      error: {
        message: error.message ?? '入力項目に誤りがあります',
      },
    };
  },
});

const EditPrivateMoney = ({
  mode,
  moneyFormValues,
  onConfirm,
  allowedInputs = 'all',
  disabled = false,
}: Props) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [moneyCreateResult, setMoneyCreateResult] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  const formMethods = useForm<PrivateMoneyFormValues>({
    defaultValues: moneyFormValues ?? PRIVATE_MONEY_FORM_DEFAULT_VALUES,
    shouldUnregister: true,
  });
  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  useEffect(() => {
    reset(moneyFormValues ?? PRIVATE_MONEY_FORM_DEFAULT_VALUES);
  }, [moneyFormValues, reset]);

  const showConfirmModal = (values: PrivateMoneyFormValues) => {
    dispatch(
      openAlertModal({
        body: (
          <AlertMessage>
            {mode === 'create' ? '登録をしてもよろしいですか？' : '更新をしてもよろしいですか？'}
          </AlertMessage>
        ),
        styles: {
          content: {
            padding: '64px 128px',
          },
        },
        confirmText: '実行',
        onConfirm: async () => {
          try {
            dispatch(showLoadingSpinner());
            await onConfirm(values);
            setMoneyCreateResult('success');
            setTimeout(() => setMoneyCreateResult(null), 5000);
          } catch (e) {
            const { key: errorKey, error } = getApiError(await Promise.resolve(e));
            setError(errorKey, error);
          } finally {
            dispatch(hideLoadingSpinner());
          }
        },
      })
    );
  };

  const isSuccess = () => moneyCreateResult === 'success';

  return (
    <>
      <ButtonGroup
        gap={1}
        buttons={TABS}
        activeIndex={selectedTabIndex}
        onClick={(_, index) => setSelectedTabIndex(index)}
      />
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(showConfirmModal)}>
          {[
            <BasicInfoForm mode={mode} allowedInputs={allowedInputs} />,
            <ChargingMethodForm allowedInputs={allowedInputs} />,
            <AdditionalSettingsForm allowedInputs={allowedInputs} />,
            <DeviceInfoForm allowedInputs={allowedInputs} />,
          ].map((subForm, index) => (
            <Hidable
              key={['basic-info', 'charging-method', 'additional-settings', 'device-info'][index]}
              hidden={selectedTabIndex !== index}
            >
              {subForm}
            </Hidable>
          ))}
          {!disabled && <Button type="submit">{mode === 'create' ? '登録' : '更新'}</Button>}
        </Form>
      </FormProvider>

      <div style={{ margin: '5px' }}>
        <Alert color="success" isOpen={isSuccess()}>
          {mode === 'create' ? 'マネーの登録に成功しました' : 'マネーの更新に成功しました'}
        </Alert>
        <Alert color="danger" isOpen={!errors?.root && Object.keys(errors).length > 0}>
          入力項目に誤りがあります
        </Alert>
        <Alert color="danger" isOpen={!!errors?.root?.unknown}>
          予期しないエラーが発生しました
        </Alert>
        <Alert color="danger" isOpen={!!errors?.root?.serverError}>
          {errors.root?.serverError?.message}
        </Alert>
      </div>
    </>
  );
};

export default EditPrivateMoney;
